import { launchContentPack } from "@stormglass/game-content";
import type { UpgradeDefinition } from "@stormglass/game-core";

import { localizer, type MessageId } from "@stormglass/game-localization";

import { useGameHost, useGameState } from "../game/GameContext";
import { WeatherIcon } from "./WeatherIcon";

const LABELS: Readonly<
  Record<string, { readonly title: MessageId; readonly description: MessageId }>
> = {
  "upgrade.lightning.damage": {
    title: "upgrade.damage",
    description: "upgrade.damage.description",
  },
  "upgrade.lightning.interval": {
    title: "upgrade.interval",
    description: "upgrade.interval.description",
  },
  "upgrade.lightning.critical-chance": {
    title: "upgrade.critical",
    description: "upgrade.critical.description",
  },
  "upgrade.lightning.critical-damage": {
    title: "upgrade.criticalDamage",
    description: "upgrade.criticalDamage.description",
  },
};

const humanize = (id: string): string =>
  id
    .split(".")
    .at(-1)
    ?.replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) ?? id;
const costAtLevel = (upgrade: UpgradeDefinition, level: number): number =>
  Math.ceil(upgrade.baseCost * Math.pow(upgrade.costGrowth, level));

export function UpgradeDock(): React.JSX.Element {
  const host = useGameHost();
  const { snapshot, ui } = useGameState();
  const upgrades = Object.values(launchContentPack.upgrades)
    .filter(
      (upgrade) =>
        upgrade.category === "basic" &&
        (upgrade.weaponKind === undefined ||
          snapshot.ownedWeapons.includes(upgrade.weaponKind)),
    )
    .slice(0, 4);
  return (
    <section className="upgrade-dock" aria-label="Upgrade controls">
      <div className="upgrade-dock__topline">
        <div className="charge-readout">
          <span className="charge-glyph">ϟ</span>
          <div>
            <small>{localizer.t("hud.charge")}</small>
            <strong>{localizer.number(Math.floor(ui.charge))}</strong>
          </div>
        </div>
        <div className="weather-tabs" aria-label="Owned weather systems">
          {snapshot.ownedWeapons.map((kind) => (
            <button
              type="button"
              key={kind}
              aria-label={`${kind} upgrades`}
              className="weather-tab is-active"
            >
              <WeatherIcon kind={kind} />
            </button>
          ))}
        </div>
        <div className="target-rule">
          <span>◎</span> Nearest core
        </div>
      </div>
      <div className="upgrade-grid">
        {upgrades.map((upgrade) => {
          const level = snapshot.runUpgradeLevels[upgrade.id] ?? 0;
          const price = costAtLevel(upgrade, level);
          const affordable = ui.charge >= price && level < upgrade.maxLevel;
          const copy = LABELS[upgrade.id];
          return (
            <button
              type="button"
              className="upgrade-card"
              key={upgrade.id}
              disabled={!affordable}
              onClick={() => {
                host.command({
                  type: "purchase-upgrade",
                  upgradeId: upgrade.id,
                });
              }}
              aria-label={`${copy === undefined ? humanize(upgrade.id) : localizer.t(copy.title)}, level ${String(level)}, costs ${String(price)} Charge`}
            >
              <span className="upgrade-card__icon">
                {upgrade.id.includes("interval")
                  ? "⌁"
                  : upgrade.id.includes("critical")
                    ? "✦"
                    : "ϟ"}
              </span>
              <span className="upgrade-card__copy">
                <strong>
                  {copy === undefined
                    ? humanize(upgrade.id)
                    : localizer.t(copy.title)}
                </strong>
                <small>
                  {copy === undefined
                    ? upgrade.effects
                        .map(
                          (effect) =>
                            `${effect.stat} ${effect.operation} ${String(effect.value)}`,
                        )
                        .join(" · ")
                    : localizer.t(copy.description)}
                </small>
              </span>
              <span className="upgrade-card__meta">
                <small>LV {level}</small>
                <strong>
                  <i>ϟ</i>
                  {price}
                </strong>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
